import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ScheduleSendIcon from "@material-ui/icons/Send";
import CheckIcon from "@material-ui/icons/Check";
import RepeatIcon from "@material-ui/icons/Repeat";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import SmsOutlinedIcon from "@material-ui/icons/SmsOutlined";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";

import MainContainer from "../../components/MainContainer";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { exibeMotivoFalha } from "../../helpers/motivoDeFalha";
import {
  scheduleKind,
  isRecurring,
  filterByCanal,
  INTERVAL_UNITS,
} from "./scheduleRules";

function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const localizer = momentLocalizer(moment);

const VIEWS = ["month", "week", "day", "agenda"];

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    schedules.forEach((schedule) => {
      const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...state, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Cores por tipo de evento (mockup: pendente ciano, enviado verde,
// recorrente roxo, falha vermelho).
const KIND_COLORS = {
  pend: AEROS.cyan,
  sent: AEROS.success,
  rec: "#C08BFF",
  err: AEROS.error,
};
const KIND_ICONS = {
  pend: ScheduleSendIcon,
  sent: CheckIcon,
  rec: RepeatIcon,
  err: ErrorOutlineIcon,
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const cellBorder = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const offRange = isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.015)";
  const gradient = theme.palette.aerosGradient;
  return {
    "@global": {
      ".rbc-overlay": {
        backgroundColor: theme.palette.aeros.surface2,
        border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
        borderRadius: 12,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        color: theme.palette.text.primary,
        padding: 8,
      },
      ".rbc-overlay-header": {
        borderBottom: `1px solid ${border}`,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: theme.palette.aeros.textTertiary,
        margin: "-8px -8px 6px",
        padding: "6px 10px",
      },
      ".rbc-overlay .rbc-event": {
        backgroundColor: "transparent",
        padding: 0,
        marginBottom: 3,
      },
    },
    // ---- header da página (60px) ----
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "0 12px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
    },
    pageTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    pageTitleCount: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    pageSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
    },
    headerActions: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    searchPill: {
      width: 220,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "5px 14px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
    },
    canalButton: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "7px 12px",
      fontSize: 12.5,
      fontWeight: 500,
      color: theme.palette.text.secondary,
      textTransform: "none",
      "& .MuiButton-endIcon": { marginLeft: 4 },
      "& .MuiButton-endIcon .MuiSvgIcon-root": { fontSize: 16 },
    },
    gradientButton: {
      borderRadius: 10,
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 700,
      "& .MuiButton-startIcon": { marginRight: 4 },
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    // ---- corpo: calendário + painel lateral ----
    pageBody: {
      flex: 1,
      padding: "16px 12px",
      overflowY: "auto",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 330px",
      gap: 14,
      alignItems: "start",
      ...theme.scrollbarStyles,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    calendarCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: 20,
      minWidth: 0,
    },
    calendarToolbar: {
      display: "flex",
      alignItems: "center",
      marginBottom: 16,
      gap: 0,
      flexWrap: "wrap",
    },
    navGroup: { display: "flex", gap: 4 },
    navButton: {
      width: 30,
      height: 30,
      borderRadius: 9,
      border: `1px solid ${borderInput}`,
      color: theme.palette.text.secondary,
      padding: 0,
      "& .MuiSvgIcon-root": { fontSize: 17 },
      "&:hover": { color: theme.palette.text.primary, backgroundColor: "transparent" },
    },
    monthLabel: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 15,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      marginLeft: 14,
      color: theme.palette.text.primary,
      textTransform: "capitalize",
    },
    toolbarRight: {
      marginLeft: "auto",
      display: "flex",
      gap: 6,
      alignItems: "center",
    },
    todayButton: {
      color: theme.palette.text.secondary,
      border: `1px solid ${borderInput}`,
      borderRadius: 99,
      padding: "3px 14px",
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
      minWidth: 0,
      lineHeight: 1.5,
    },
    segmented: {
      display: "flex",
      border: `1px solid ${borderInput}`,
      borderRadius: 99,
      overflow: "hidden",
      fontSize: 12,
      fontWeight: 600,
    },
    segment: {
      padding: "4px 14px",
      color: theme.palette.text.secondary,
      cursor: "pointer",
      border: "none",
      background: "transparent",
      font: "inherit",
      fontWeight: 600,
      lineHeight: 1.5,
      "&:hover": { color: theme.palette.text.primary },
    },
    segmentActive: {
      backgroundColor: "rgba(160,68,255,0.2)",
      color: stateText(theme, "#C08BFF", "purple"),
      "&:hover": { color: stateText(theme, "#C08BFF", "purple") },
    },
    // ---- react-big-calendar no tema ----
    calendar: {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      "& .rbc-month-view": {
        border: "none",
        borderTop: `1px solid ${cellBorder}`,
        borderLeft: `1px solid ${cellBorder}`,
      },
      "& .rbc-month-header": {
        marginTop: -1,
        marginLeft: -1,
      },
      "& .rbc-header": {
        border: "none",
        padding: "8px 0",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: theme.palette.aeros.textTertiary,
        "& + .rbc-header": { border: "none" },
      },
      "& .rbc-month-row": {
        border: "none",
        minHeight: 92,
        overflow: "visible",
        "& + .rbc-month-row": { border: "none" },
      },
      "& .rbc-day-bg": {
        borderRight: `1px solid ${cellBorder}`,
        borderBottom: `1px solid ${cellBorder}`,
        "& + .rbc-day-bg": { borderLeft: "none" },
      },
      "& .rbc-off-range-bg": { backgroundColor: offRange },
      "& .rbc-today": { backgroundColor: "rgba(160,68,255,0.05)" },
      "& .rbc-date-cell": {
        padding: "6px 6px 0",
        fontSize: 11.5,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        color: theme.palette.text.secondary,
        "& .rbc-button-link": {
          padding: "3px 5px",
          color: "inherit",
          font: "inherit",
          borderRadius: 6,
        },
      },
      "& .rbc-off-range": { color: theme.palette.aeros.textTertiary },
      "& .rbc-date-cell.rbc-now .rbc-button-link": {
        width: 22,
        height: 22,
        padding: 0,
        borderRadius: "50%",
        backgroundImage: gradient,
        color: "#07080D",
        fontWeight: 800,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      },
      "& .rbc-row-segment": { padding: "0 6px 3px" },
      "& .rbc-event": {
        backgroundColor: "transparent",
        padding: 0,
        borderRadius: 5,
        outline: "none",
        "&:focus": { outline: "none" },
        "&.rbc-selected": { backgroundColor: "transparent" },
      },
      "& .rbc-event-content": { overflow: "visible" },
      "& .rbc-show-more": {
        backgroundColor: "transparent",
        color: stateText(theme, AEROS.cyan, "info"),
        fontSize: 10,
        fontWeight: 600,
        padding: "1px 6px",
      },
      // semana / dia
      "& .rbc-time-view": {
        border: `1px solid ${cellBorder}`,
        borderRadius: 10,
        overflow: "hidden",
      },
      "& .rbc-time-view .rbc-header": { borderBottom: `1px solid ${cellBorder}` },
      "& .rbc-time-header-content": { borderLeft: `1px solid ${cellBorder}` },
      "& .rbc-time-header.rbc-overflowing": { borderRight: "none" },
      "& .rbc-time-content": { borderTop: `2px solid ${cellBorder}` },
      "& .rbc-time-content > * + * > *": { borderLeft: `1px solid ${cellBorder}` },
      "& .rbc-timeslot-group": { borderBottom: `1px solid ${cellBorder}` },
      "& .rbc-day-slot .rbc-time-slot": { borderTop: `1px solid ${alpha(cellBorder, 0.3)}` },
      "& .rbc-allday-cell": { display: "none" },
      "& .rbc-time-header-gutter, & .rbc-time-gutter": {
        backgroundColor: theme.palette.aeros.surface,
      },
      "& .rbc-label": {
        fontSize: 11,
        color: theme.palette.text.secondary,
        fontVariantNumeric: "tabular-nums",
      },
      "& .rbc-current-time-indicator": { backgroundColor: AEROS.cyan, height: 2 },
      "& .rbc-day-slot .rbc-event": {
        border: "none",
        backgroundColor: "transparent",
        padding: 0,
      },
      "& .rbc-day-slot .rbc-event-label": { display: "none" },
      "& .rbc-day-slot .rbc-events-container": { marginRight: 4 },
      "& .rbc-time-view .rbc-today": { backgroundColor: "rgba(160,68,255,0.05)" },
      // agenda
      "& .rbc-agenda-view table.rbc-agenda-table": {
        border: `1px solid ${cellBorder}`,
        borderRadius: 10,
        borderCollapse: "separate",
        borderSpacing: 0,
        overflow: "hidden",
      },
      "& .rbc-agenda-view table.rbc-agenda-table thead > tr > th": {
        border: "none",
        borderBottom: `1px solid ${cellBorder}`,
        padding: "10px 12px",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: theme.palette.aeros.textTertiary,
        backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
      },
      "& .rbc-agenda-view table.rbc-agenda-table tbody > tr > td": {
        border: "none",
        borderTop: `1px solid ${cellBorder}`,
        padding: "9px 12px",
        fontSize: 12.5,
        verticalAlign: "middle",
      },
      "& .rbc-agenda-view table.rbc-agenda-table tbody > tr:first-child > td": {
        borderTop: "none",
      },
      "& .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td": {
        borderLeft: `1px solid ${cellBorder}`,
      },
      "& .rbc-agenda-view table.rbc-agenda-table tbody > tr:hover": {
        backgroundColor: "rgba(160,68,255,0.04)",
      },
      "& .rbc-agenda-date-cell, & .rbc-agenda-time-cell": {
        color: theme.palette.text.secondary,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      },
      "& .rbc-agenda-empty": {
        padding: "28px 0",
        textAlign: "center",
        fontSize: 12.5,
        color: theme.palette.aeros.textTertiary,
      },
    },
    // ---- chip de evento ----
    eventChip: {
      display: "flex",
      alignItems: "center",
      gap: 5,
      borderLeft: "2px solid",
      borderRadius: 5,
      padding: "2.5px 6px",
      overflow: "hidden",
      minWidth: 0,
      position: "relative",
      "& .MuiSvgIcon-root": { fontSize: 11, flex: "none" },
      "&:hover $eventActions": { opacity: 1 },
    },
    eventLabel: {
      fontSize: 10.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      flex: 1,
      minWidth: 0,
    },
    eventActions: {
      display: "flex",
      gap: 2,
      opacity: 0,
      transition: "opacity 0.2s",
      "& .MuiSvgIcon-root": { fontSize: 12, color: theme.palette.text.secondary },
      "& .MuiSvgIcon-root:hover": { color: theme.palette.text.primary },
    },
    legend: {
      display: "flex",
      gap: 16,
      marginTop: 14,
      alignItems: "center",
      flexWrap: "wrap",
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11.5,
      color: theme.palette.text.secondary,
    },
    legendDot: { width: 8, height: 8, borderRadius: 3 },
    // ---- painel lateral ----
    sidePanel: { display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },
    kpiGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
    kpiCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "13px 16px",
    },
    kpiValue: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 20,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      lineHeight: 1.2,
    },
    kpiValueError: { color: stateText(theme, AEROS.error, "error") },
    kpiLabel: { fontSize: 11.5, color: theme.palette.text.secondary },
    upcomingCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    upcomingHead: {
      padding: "14px 16px 10px",
      display: "flex",
      alignItems: "center",
    },
    upcomingTitle: { fontSize: 13.5, fontWeight: 700, color: theme.palette.text.primary },
    upcomingLink: {
      marginLeft: "auto",
      fontSize: 11.5,
      color: stateText(theme, AEROS.cyan, "info"),
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      font: "inherit",
      fontWeight: 600,
    },
    upcomingRow: {
      padding: "11px 16px",
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      display: "flex",
      gap: 11,
      alignItems: "flex-start",
      "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" },
    },
    dayBox: {
      flex: "none",
      width: 40,
      textAlign: "center",
      backgroundColor: isLight ? "rgba(15,20,40,0.03)" : "rgba(255,255,255,0.03)",
      border: `1px solid ${border}`,
      borderRadius: 9,
      padding: "5px 0",
    },
    dayBoxWeekday: {
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: stateText(theme, AEROS.cyan, "info"),
    },
    dayBoxWeekdayError: { color: stateText(theme, AEROS.error, "error") },
    dayBoxDay: {
      fontSize: 15,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    upcomingBody: { flex: 1, minWidth: 0 },
    upcomingTop: { display: "flex", alignItems: "baseline", gap: 6 },
    upcomingContact: {
      fontSize: 12.5,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: theme.palette.text.primary,
    },
    upcomingTime: {
      marginLeft: "auto",
      fontSize: 10.5,
      color: theme.palette.aeros.textTertiary,
      fontVariantNumeric: "tabular-nums",
      flex: "none",
    },
    upcomingMsg: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      marginTop: 2,
    },
    upcomingMeta: { display: "flex", gap: 5, marginTop: 6, alignItems: "center" },
    miniChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 9.5,
      fontWeight: 600,
      borderRadius: 5,
      padding: "1px 7px",
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 11 },
    },
    chipWhatsapp: {
      color: stateText(theme, AEROS.success, "success"),
      backgroundColor: alpha(AEROS.success, 0.1),
    },
    chipSms: {
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.1),
    },
    chipRecurring: {
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: "rgba(160,68,255,0.12)",
    },
    chipError: {
      color: stateText(theme, AEROS.error, "error"),
      backgroundColor: alpha(AEROS.error, 0.1),
    },
    rowActions: { marginLeft: "auto", display: "flex", gap: 4 },
    rowActionButton: {
      padding: 0,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 15 },
      "&:hover": { backgroundColor: "transparent", color: AEROS.cyan },
    },
    rowActionDanger: {
      "&:hover": { color: AEROS.error },
    },
    emptyUpcoming: {
      padding: "24px 16px",
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      textAlign: "center",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
    },
  };
});

const recurrenceLabel = (schedule) => {
  const unit = INTERVAL_UNITS[Number(schedule.intervalo)] || "days";
  const value = Number(schedule.valorIntervalo) || 1;
  if (value === 1) return i18n.t(`schedules.recurrence.every.${unit}`);
  return i18n.t(`schedules.recurrence.everyN.${unit}`, { count: value });
};

const Schedules = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, thisWeek: 0, failed: 0, upcoming: [] });
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const [canalFilter, setCanalFilter] = useState("all");
  const [canalAnchor, setCanalAnchor] = useState(null);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error(
          "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
        );
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  // Período visível do calendário (mês com as semanas de borda; semana/dia
  // pela semana; agenda pelo mês) — o backend devolve tudo do intervalo.
  const range = useMemo(() => {
    const m = moment(date);
    const unit = view === "week" || view === "day" ? "week" : "month";
    const start = m.clone().startOf(unit).startOf("week");
    const end = m.clone().endOf(unit).endOf("week");
    return {
      dateFrom: start.format("YYYY-MM-DD HH:mm:ss"),
      dateTo: end.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [date, view]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, canal: canalFilter, ...range },
      });

      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [searchParam, canalFilter, range]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules/summary", {
        params: { canal: canalFilter },
      });
      setSummary(data);
    } catch (err) {
      toastError(err);
    }
  }, [canalFilter]);

  const reloadAll = useCallback(() => {
    fetchSchedules();
    fetchSummary();
  }, [fetchSchedules, fetchSummary]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchSchedules, contactId, handleOpenScheduleModalFromContactId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    let timer = null;
    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
      // KPIs e próximos envios vêm do servidor: recalcula após a rajada
      clearTimeout(timer);
      timer = setTimeout(fetchSummary, 400);
    };

    socket.on(`company${user.companyId}-schedule`, onCompanySchedule);

    return () => {
      clearTimeout(timer);
      socket.off(`company${user.companyId}-schedule`, onCompanySchedule);
    };
  }, [socket, user.companyId, fetchSummary]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    reloadAll();
  };

  const askDelete = (schedule) => {
    setDeletingSchedule(schedule);
    setConfirmModalOpen(true);
  };

  // O servidor já filtra por canal; o filtro local só cobre o que o socket
  // acrescentou depois (create/update chegam sem passar pelo filtro).
  const visibleSchedules = useMemo(
    () => filterByCanal(schedules, canalFilter),
    [schedules, canalFilter]
  );

  const weekCount = summary.thisWeek;
  const failedCount = summary.failed;
  const upcoming = summary.upcoming || [];

  const events = useMemo(
    () =>
      visibleSchedules.map((schedule) => ({
        id: schedule.id,
        title: schedule?.contact?.name || "",
        start: new Date(schedule.sendAt),
        end: new Date(schedule.sendAt),
        resource: schedule,
      })),
    [visibleSchedules]
  );

  const calendarMessages = useMemo(
    () => ({
      date: i18n.t("schedules.date"),
      time: i18n.t("schedules.time"),
      event: i18n.t("schedules.event"),
      allDay: i18n.t("schedules.allDay"),
      week: i18n.t("schedules.week"),
      work_week: i18n.t("schedules.work_week"),
      day: i18n.t("schedules.day"),
      month: i18n.t("schedules.month"),
      previous: i18n.t("schedules.previous"),
      next: i18n.t("schedules.next"),
      yesterday: i18n.t("schedules.yesterday"),
      tomorrow: i18n.t("schedules.tomorrow"),
      today: i18n.t("schedules.today"),
      agenda: i18n.t("schedules.agenda"),
      noEventsInRange: i18n.t("schedules.noEventsInRange"),
      showMore: (total) => i18n.t("schedules.showMore", { count: total }),
    }),
    []
  );

  const EventChip = useCallback(
    ({ event }) => {
      const schedule = event.resource;
      const kind = scheduleKind(schedule);
      const color = KIND_COLORS[kind];
      const Icon = KIND_ICONS[kind];
      return (
        <div
          className={classes.eventChip}
          style={{ backgroundColor: alpha(color, kind === "rec" ? 0.1 : 0.08), borderLeftColor: color }}
          // Na falha, o motivo é a informação útil — o corpo da mensagem o
          // usuário já conhece.
          title={
            kind === "err" && schedule.failureReason
              ? i18n.t("schedules.failureTooltip", {
                  reason: exibeMotivoFalha(schedule.failureReason).resumo,
                })
              : schedule.body
          }
        >
          <Icon style={{ color }} />
          <span className={classes.eventLabel}>{event.title}</span>
          <span className={classes.eventActions}>
            <DeleteOutlineIcon
              onClick={(e) => {
                e.stopPropagation();
                askDelete(schedule);
              }}
            />
          </span>
        </div>
      );
    },
    [classes]
  );

  const toolbarLabel = useMemo(() => {
    const m = moment(date);
    if (view === "month") return m.format("MMMM YYYY");
    if (view === "week") {
      const start = m.clone().startOf("week");
      const end = m.clone().endOf("week");
      return `${start.format("DD MMM")} – ${end.format("DD MMM YYYY")}`;
    }
    if (view === "day") return m.format("dddd, DD [de] MMMM");
    return m.format("MMMM YYYY");
  }, [date, view]);

  const navigate = (direction) => {
    const unit = view === "agenda" ? "month" : view;
    setDate(moment(date).add(direction, unit).toDate());
  };

  const weeksInMonth = useMemo(() => {
    const start = moment(date).startOf("month").startOf("week");
    const end = moment(date).endOf("month").endOf("week");
    return Math.round(end.diff(start, "days") / 7);
  }, [date]);

  const calendarHeight = view === "month" ? weeksInMonth * 93 + 34 : 620;

  const canalLabel =
    canalFilter === "all"
      ? i18n.t("schedules.canal.all")
      : canalFilter === "sms"
      ? "SMS"
      : "WhatsApp";

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t("schedules.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={reloadAll}
          scheduleId={selectedSchedule ? selectedSchedule.id : null}
          contactId={contactId}
          cleanContact={cleanContact}
          user={user}
        />
      )}

      <div className={classes.pageHeader}>
        <div>
          <Typography component="div" className={classes.pageTitle}>
            {i18n.t("schedules.title")}{" "}
            <span className={classes.pageTitleCount}>· {summary.total}</span>
          </Typography>
          <div className={classes.pageSubtitle}>
            {i18n.t("schedules.subtitleWeek", { count: weekCount })}
          </div>
        </div>
        <div className={classes.headerActions}>
          <InputBase
            className={classes.searchPill}
            placeholder={i18n.t("schedules.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("schedules.searchPlaceholder") }}
          />
          <Button
            className={classes.canalButton}
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setCanalAnchor(e.currentTarget)}
            aria-haspopup="true"
          >
            {i18n.t("schedules.canal.label")}: {canalLabel}
          </Button>
          <Menu
            anchorEl={canalAnchor}
            open={Boolean(canalAnchor)}
            onClose={() => setCanalAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            {[
              ["all", i18n.t("schedules.canal.all")],
              ["whatsapp", "WhatsApp"],
              ["sms", "SMS"],
            ].map(([value, label]) => (
              <MenuItem
                key={value}
                selected={canalFilter === value}
                onClick={() => {
                  setCanalFilter(value);
                  setCanalAnchor(null);
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
          <Button
            variant="contained"
            color="primary"
            className={classes.gradientButton}
            startIcon={<AddIcon />}
            onClick={handleOpenScheduleModal}
          >
            {i18n.t("schedules.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={classes.pageBody}>
        <div className={classes.calendarCard}>
          <div className={classes.calendarToolbar}>
            <div className={classes.navGroup}>
              <IconButton
                className={classes.navButton}
                onClick={() => navigate(-1)}
                aria-label={i18n.t("schedules.previous")}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                className={classes.navButton}
                onClick={() => navigate(1)}
                aria-label={i18n.t("schedules.next")}
              >
                <ChevronRightIcon />
              </IconButton>
            </div>
            <div className={classes.monthLabel}>{toolbarLabel}</div>
            <div className={classes.toolbarRight}>
              <Button className={classes.todayButton} onClick={() => setDate(new Date())}>
                {i18n.t("schedules.today")}
              </Button>
              <div className={classes.segmented} role="tablist">
                {VIEWS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="tab"
                    aria-selected={view === v}
                    className={`${classes.segment} ${view === v ? classes.segmentActive : ""}`}
                    onClick={() => setView(v)}
                  >
                    {i18n.t(`schedules.${v}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <Calendar
            className={classes.calendar}
            localizer={localizer}
            messages={calendarMessages}
            formats={{
              agendaDateFormat: "DD/MM ddd",
              weekdayFormat: "ddd",
              dayFormat: "ddd DD",
              timeGutterFormat: "HH:mm",
              eventTimeRangeFormat: ({ start }) => moment(start).format("HH:mm"),
              agendaTimeFormat: "HH:mm",
              agendaTimeRangeFormat: ({ start }) => moment(start).format("HH:mm"),
            }}
            events={events}
            startAccessor="start"
            endAccessor="end"
            toolbar={false}
            popup
            view={view}
            views={VIEWS}
            onView={setView}
            date={date}
            onNavigate={setDate}
            onSelectEvent={(event) => handleEditSchedule(event.resource)}
            components={{ event: EventChip }}
            style={{ height: calendarHeight }}
          />

          <div className={classes.legend}>
            {["pend", "sent", "rec", "err"].map((kind) => (
              <div key={kind} className={classes.legendItem}>
                <div className={classes.legendDot} style={{ backgroundColor: KIND_COLORS[kind] }} />
                {i18n.t(`schedules.legend.${kind}`)}
              </div>
            ))}
          </div>
        </div>

        <div className={classes.sidePanel}>
          <div className={classes.kpiGrid}>
            <div className={classes.kpiCard}>
              <div className={classes.kpiValue}>{weekCount}</div>
              <div className={classes.kpiLabel}>{i18n.t("schedules.kpi.thisWeek")}</div>
            </div>
            <div className={classes.kpiCard}>
              <div className={`${classes.kpiValue} ${classes.kpiValueError}`}>{failedCount}</div>
              <div className={classes.kpiLabel}>{i18n.t("schedules.kpi.failed")}</div>
            </div>
          </div>

          <div className={classes.upcomingCard}>
            <div className={classes.upcomingHead}>
              <div className={classes.upcomingTitle}>{i18n.t("schedules.upcoming.title")}</div>
              <button
                type="button"
                className={classes.upcomingLink}
                onClick={() => setView("agenda")}
              >
                {i18n.t("schedules.upcoming.seeAgenda")} →
              </button>
            </div>
            {upcoming.length === 0 && (
              <div className={classes.emptyUpcoming}>{i18n.t("schedules.upcoming.empty")}</div>
            )}
            {upcoming.map((schedule) => {
              const kind = scheduleKind(schedule);
              const at = moment(schedule.sendAt);
              const isSms = (schedule.canal || "whatsapp") === "sms";
              return (
                <div key={schedule.id} className={classes.upcomingRow}>
                  <div className={classes.dayBox}>
                    <div
                      className={`${classes.dayBoxWeekday} ${
                        kind === "err" ? classes.dayBoxWeekdayError : ""
                      }`}
                    >
                      {at.format("ddd").replace(".", "")}
                    </div>
                    <div className={classes.dayBoxDay}>{at.format("DD")}</div>
                  </div>
                  <div className={classes.upcomingBody}>
                    <div className={classes.upcomingTop}>
                      <span className={classes.upcomingContact}>
                        {schedule?.contact?.name}
                      </span>
                      <span className={classes.upcomingTime}>{at.format("HH:mm")}</span>
                    </div>
                    <div className={classes.upcomingMsg}>{schedule.body}</div>
                    <div className={classes.upcomingMeta}>
                      <span
                        className={`${classes.miniChip} ${isSms ? classes.chipSms : classes.chipWhatsapp}`}
                      >
                        {isSms ? <SmsOutlinedIcon /> : <ChatBubbleOutlineIcon />}
                        {isSms ? "SMS" : "WhatsApp"}
                      </span>
                      {isRecurring(schedule) && (
                        <span className={`${classes.miniChip} ${classes.chipRecurring}`}>
                          <RepeatIcon />
                          {recurrenceLabel(schedule)}
                        </span>
                      )}
                      {kind === "err" && (
                        <Tooltip
                          title={
                            schedule.failureReason
                              ? i18n.t("schedules.failureTooltip", {
                                  reason: exibeMotivoFalha(schedule.failureReason).resumo,
                                })
                              : i18n.t("scheduleModal.failure.unknown")
                          }
                        >
                          <span className={`${classes.miniChip} ${classes.chipError}`}>
                            <ErrorOutlineIcon />
                            {i18n.t("schedules.legend.failedShort")}
                          </span>
                        </Tooltip>
                      )}
                      <div className={classes.rowActions}>
                        <Tooltip title={i18n.t("schedules.actions.edit")}>
                          <IconButton
                            className={classes.rowActionButton}
                            onClick={() => handleEditSchedule(schedule)}
                            size="small"
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t("schedules.actions.delete")}>
                          <IconButton
                            className={`${classes.rowActionButton} ${classes.rowActionDanger}`}
                            onClick={() => askDelete(schedule)}
                            size="small"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default Schedules;
